
import React, { FC, useMemo, useState } from "react";
import DocumentTitle from "react-document-title";
import moment from "moment";
import classNames from "classnames";
import { OrganizationSecondaryNav } from "components/OrgSecondaryNav";
import { useGetOrgAnalyticsQuery } from "generated/graphql";
import { OrgAnalyticsUsers } from "./OrgAnalyticsUsers";
import { PageAlert, TitleHeader } from "@xapp/components";
import { DatePickerWrapper } from "components/DatePickerWrapper";
import { format } from 'date-fns';
import grid from "styles/grid.scss";
import style from "./style.scss";

export interface DateRangeSelectItem {
    label: string;
    value: string;
}

export interface AnalyticsProps {
    readonly organizationId?: string;
    readonly startDate?: Date;
    readonly endDate?: Date;
    /**
     * IF the analytics are embedded within another page, it will hide the title and the graph
     */
    readonly embedded?: boolean;
}

export interface AnalyticsState {
    end: Date;
    interval: string;
    selectValue: string;
    start: Date;
}

export const OrgAnalyticsContainer: FC<AnalyticsProps> = ({
    organizationId,
    embedded,
    startDate,
    endDate
}: AnalyticsProps) => {

    const now = useMemo(() => moment(), []);

    // 90 days from now
    const minDate = useMemo(() => now.clone().add(-90, "days").toDate(), [now]);
    // Today!
    const maxDate = useMemo(() => now.toDate(), [now]);

    const disabledDays = [
        { after: new Date(maxDate) },
        { before: new Date(minDate) }
    ];

    const [start, setStart] = useState<Date>(startDate ? startDate : now.clone().add(-1, "week").toDate());
    const [end, setEnd] = useState<Date>(endDate ? endDate : now.toDate());

    const handleDateSubmit = (start: Date, end: Date) => {
        setStart(start);
        setEnd(end);
    };

    const formattedDateRange = `${format(start, 'yyyy/MM/dd')} - ${format(end, 'yyyy/MM/dd')}`;

    const [response] = useGetOrgAnalyticsQuery({
        requestPolicy: "network-only",
        variables: {
            endDate: end.toISOString(),
            orgId: organizationId,
            startDate: start.toISOString(),
        }
    });

    const { data, error, fetching } = response;

    if (error) {
        return (
            <PageAlert background="orange">
                <div>
                    Error loading analytics, there might be too much data.  Adjust your date ranges or try again later.
                </div>
            </PageAlert>
        );
    }

    const totals = data?.org?.analytics?.user;
    const intervals = data?.org?.analytics?.user?.intervals;

    return (
        <>
            {embedded ? (
                <div id="app_users_analytics_graph" className={classNames(grid.flexwrap, style.wrap)}>
                    <div className={grid.row}>
                        <div className={grid.whole}>
                            <OrgAnalyticsUsers organizationId={organizationId} intervals={intervals} embedded={embedded} fetching={fetching} />
                        </div>
                    </div>
                </div>
            ) : (
                <DocumentTitle title={`XAPP | ${organizationId} - Analytics`}>
                    <section>
                        <p>{`${organizationId} > Analytics`}</p>
                        <TitleHeader static={true} value="Analytics">
                            <div className={style.picker}>
                                <DatePickerWrapper
                                    mode="range"
                                    dateValue={formattedDateRange}
                                    onSubmitDate={handleDateSubmit}
                                    disabled={disabledDays}
                                />
                            </div>
                        </TitleHeader>

                        <div id="app_users_analytics_graph" className={classNames(grid.flexwrap, style.wrap)}>
                            <div className={grid.row}>
                                <div className={grid.whole}>
                                    <OrgAnalyticsUsers organizationId={organizationId} intervals={intervals} totals={totals} embedded={embedded} fetching={fetching} />
                                </div>
                            </div>
                        </div>

                        <OrganizationSecondaryNav organizationId={organizationId} />
                    </section>
                </DocumentTitle>
            )}
        </>
    );
};